import React from 'react';
import { DiffHunk } from '../../types';
import { LineFoldControl } from './LineFoldControl';

interface UnifiedDiffViewProps {
  hunks: DiffHunk[];
  foldUnchanged: boolean;
  activeHunkIndex: number;
}

export const UnifiedDiffView: React.FC<UnifiedDiffViewProps> = ({
  hunks,
  foldUnchanged,
  activeHunkIndex,
}) => {
  return (
    <div className="w-full overflow-x-auto text-xs font-mono select-text">
      <table className="w-full border-collapse table-fixed min-w-[600px]">
        <colgroup>
          <col className="w-12 border-r border-slate-200 dark:border-slate-800" />
          <col className="w-12 border-r border-slate-200 dark:border-slate-800" />
          <col className="w-8 border-r border-slate-200 dark:border-slate-800 text-center" />
          <col className="w-full" />
        </colgroup>

        <thead className="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 font-sans font-semibold text-[11px]">
          <tr>
            <th className="py-2.5 px-2 text-right">Old</th>
            <th className="py-2.5 px-2 text-right">New</th>
            <th className="py-2.5 px-1 text-center">+/-</th>
            <th className="py-2.5 px-3 text-left">Unified Policy Stream</th>
          </tr>
        </thead>

        <tbody>
          {hunks.map((hunk, hIdx) => {
            const isUnchanged = hunk.changeType === 'unchanged' || hunk.change_type === 'unchanged';
            if (isUnchanged && foldUnchanged && hunk.lines.length > 8) {
              return (
                <LineFoldControl
                  key={`hunk-${hIdx}`}
                  lineCount={hunk.lines.length}
                  lines={hunk.lines}
                  mode="unified"
                />
              );
            }

            const heading = hunk.sectionHeading || hunk.section_heading;

            return (
              <React.Fragment key={`hunk-${hIdx}`}>
                {heading && (
                  <tr className="bg-slate-100 dark:bg-slate-800/90 font-sans text-slate-700 dark:text-slate-300 font-semibold border-y border-slate-200 dark:border-slate-700">
                    <td colSpan={4} className="py-1.5 px-4 text-xs">
                      § {heading}
                    </td>
                  </tr>
                )}

                {hunk.lines.map((line, lIdx) => {
                  const isDel = line.type === '-' || line.type === 'deletion' || line.type === 'deleted';
                  const isAdd = line.type === '+' || line.type === 'addition' || line.type === 'added';
                  const wordTokens = line.word_tokens || line.wordTokens;
                  const oldNo = line.old_lineno || line.oldLineNumber;
                  const newNo = line.new_lineno || line.newLineNumber;

                  return (
                    <tr
                      key={`line-${hIdx}-${lIdx}`}
                      className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${
                        isDel
                          ? 'bg-rose-50/80 dark:bg-rose-950/20 text-rose-900 dark:text-rose-200'
                          : isAdd
                          ? 'bg-emerald-50/80 dark:bg-emerald-950/20 text-emerald-900 dark:text-emerald-200'
                          : 'text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      {/* Old Line Number */}
                      <td className="py-1 px-2 text-right text-slate-400 select-none border-r border-slate-200 dark:border-slate-800">
                        {oldNo || ''}
                      </td>

                      {/* New Line Number */}
                      <td className="py-1 px-2 text-right text-slate-400 select-none border-r border-slate-200 dark:border-slate-800">
                        {newNo || ''}
                      </td>

                      {/* Sign Indicator */}
                      <td className="py-1 px-1 text-center font-bold select-none border-r border-slate-200 dark:border-slate-800">
                        {isDel ? '-' : isAdd ? '+' : ' '}
                      </td>

                      {/* Content */}
                      <td className="py-1 px-3 whitespace-pre-wrap break-words">
                        {wordTokens ? (
                          <span>
                            {wordTokens.map((wt, wIdx) => {
                              const isTokenDel = wt.type === 'del' || wt.type === 'deleted';
                              const isTokenAdd = wt.type === 'add' || wt.type === 'added';

                              return (
                                <span
                                  key={wIdx}
                                  className={
                                    isTokenDel
                                      ? 'bg-rose-200 dark:bg-rose-900/70 text-rose-950 dark:text-rose-100 rounded px-0.5 font-semibold'
                                      : isTokenAdd
                                      ? 'bg-emerald-200 dark:bg-emerald-900/70 text-emerald-950 dark:text-emerald-100 rounded px-0.5 font-semibold'
                                      : ''
                                  }
                                >
                                  {wt.text}
                                </span>
                              );
                            })}
                          </span>
                        ) : (
                          <span>{line.content}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
